import { useMemo } from 'react';
import { z } from 'zod';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { FELLOWS_CORNER_GUIDELINES } from '@/constants/fellowsCorner';
import { useFeatureFlagValue } from '@/hooks/useFeatureFlag';

/**
 * Readonly throughout so the shipped copy, frozen with `as const`, satisfies the
 * same type as a parsed remote value. Callers only read either one.
 */
export const fellowsGuidelinesSchema = z.object({
  title: z.string().min(1),
  intro: z.string().min(1),
  sections: z
    .array(
      z.object({
        heading: z.string().min(1),
        items: z.array(z.string().min(1)).min(1).readonly(),
      })
    )
    .min(1)
    .readonly(),
  disclaimer: z.string().min(1),
});

export type FellowsGuidelines = z.infer<typeof fellowsGuidelinesSchema>;

/** The guidelines live beside the questions on the one Fellows Corner value. */
const contentSchema = z.object({ guidelines: fellowsGuidelinesSchema });

/**
 * The mentorship guidelines, overridable through the Fellows Corner flag's
 * value so wording can change without a release.
 *
 * Authored by hand in a console, so the value is parsed rather than trusted:
 * anything malformed, partial, or not shaped like the schema falls back to the
 * in-repo copy rather than rendering a half-empty modal. The flag's default is
 * the boolean that gates the feature, which fails this parse and lands on the
 * same fallback.
 *
 * Falling back is the opposite of what the seed questions do, deliberately.
 * Guidelines nobody has edited still say something true, so shipped copy is the
 * right floor. Questions nobody approved should not appear at all, so an absent
 * value there means no Questions tab.
 *
 * The disclaimer travels with the rest, which is the point and the risk: it
 * makes a legal claim, so whoever edits this value is editing that claim.
 */
export function useFellowsGuidelines(): FellowsGuidelines {
  const flagValue = useFeatureFlagValue(FEATURE_FLAGS.FELLOWS_CORNER);

  return useMemo(() => {
    const parsed = contentSchema.safeParse(flagValue);

    return parsed.success ? parsed.data.guidelines : FELLOWS_CORNER_GUIDELINES;
  }, [flagValue]);
}
